import useHitarea from '@packages/systems/ui/hooks/useHitarea';
import {type LinkProps} from './Link.types';

// @todo Create a package for keycode mapping.
const ENTER = 13;
const SPACE = 32;

type ActivationHandlersProps = {
  onClick?: (arg1: any) => void;
  onKeyDown?: (arg1: any) => void;
};

/**
 * Provides better support for device engagement for activating links without
 * `href` props, specifically allow for `ENTER` or `SPACE` key presses
 * to trigger the `onClick` callback.
 *
 * The term "activation" is used instead of traditional verbs like `onClick`
 * as we recognize that engaging with UI can happen from varying device inputs.
 *
 * @todo Perhaps abstract to a common utility in the future.
 */
function useActivationHandlers(props: ActivationHandlersProps) {
  const {onClick, onKeyDown} = props;

  const handleOnClick = (event: any) => onClick && onClick(event);

  const handleOnKeyDown = (event: any) => {
    const {keyCode} = event;
    if (keyCode === ENTER || keyCode === SPACE) {
      handleOnClick(event);
    }

    onKeyDown && onKeyDown(event);
  };

  return {
    onClick: handleOnClick,
    onKeyDown: handleOnKeyDown,
  };
}

/**
 * Consolidates the event handlers between the Hitarea system (styling) and
 * ActivationHandlers (improved keyboard handling).
 */
function useEventHandlers(props: LinkProps) {
  const {
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onFocus,
    onBlur,
    onKeyDown,
    onClick,
    onKeyUp,
  } = props;

  const [hitareaState, hitareaHandlers] = useHitarea({
    onKeyUp,
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onFocus,
    onBlur,
  });
  const activationHandlers = useActivationHandlers({
    ...hitareaHandlers,
    onKeyDown,
    onClick,
  });

  const handlers = {
    ...hitareaHandlers,
    ...activationHandlers,
  } as const;

  return [hitareaState, handlers];
}

export function useLink(props: LinkProps) {
  const {
    isExternal = false,
    href,
    onMouseEnter,
    onMouseOver,
    onClick,
    onMouseLeave,
    onKeyDown,
    onKeyUp,
    onFocus,
    onBlur,
    role,
    tabIndex,
    ...rest
  } = props;

  const [handlerState, handlers] = useEventHandlers({
    onMouseEnter,
    onMouseOver,
    onMouseLeave,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    onClick,
  });

  const roleLink = !href ? 'link' : null;

  return {
    ...rest,
    ...handlerState,
    ...handlers,
    href,
    isExternal,
    role: role || roleLink,
    tabIndex: tabIndex || Boolean(roleLink) ? 0 : null,
  };
}
